import type {
  AgentProviderId,
  BridgeConfig,
  ExecutionMode,
  ModelCatalogEntry,
  ProviderDefinition,
  SessionConfig,
} from '@web/api/types'

import {
  type PillGroup,
  PillMenu,
  type PillOption,
} from '@web/components/ui/PillMenu'
import {
  EFFORTS,
  EXECUTIONS,
  modelIcon,
  PERMISSIONS,
} from '@web/components/ui/pillCatalog'
import { modelLabel } from '@web/lib/format'

export function environmentProviders(
  config: BridgeConfig,
  environment?: string,
): ProviderDefinition[] {
  return (
    config.environments.find(
      (item) => item.name === (environment ?? config.defaults.environment),
    )?.health.providers ??
    config.providers ??
    []
  )
}
export function launchSelection(
  config: BridgeConfig,
  settings: Partial<SessionConfig>,
) {
  const environment =
    settings.environment ??
    config.defaults.environment ??
    config.environments[0]?.name ??
    ''
  const providers = environmentProviders(config, environment)
  const provider = settings.provider
    ? providers.find((item) => item.id === settings.provider)
    : (providers.find((item) => item.id === config.defaults.provider) ??
      providers[0])
  const modes = provider?.execution_modes ?? []
  const mode =
    modes.find((item) => item.id === settings.execution_mode) ??
    modes.find(
      (item) => item.id === provider?.default_execution_mode && item.available,
    ) ??
    modes.find((item) => item.available)
  return {
    environment,
    providers,
    provider,
    mode,
    available:
      !!provider &&
      !!mode?.available &&
      (provider.id !== 'puck' || !!provider.models?.length),
  }
}
export const usesWorkspace = (mode?: ExecutionMode | null) =>
  mode === 'docker' || mode === 'kubernetes'

/** Providers whose private workspaces clone GitHub repositories at startup. */
export const REPOSITORY_PROVIDERS: ReadonlySet<string> = new Set([
  'claude',
  'codex',
  'opencode',
])

/**
 * Whether the launcher may offer a repository: the provider must support
 * checkouts at all (`supported`), and the chosen execution must give the
 * session a workspace to clone into (`available`).
 */
export function repositorySupport(selection: {
  provider?: { id: string }
  mode?: { id: ExecutionMode }
}): { supported: boolean; available: boolean; reason: string } {
  const supported = REPOSITORY_PROVIDERS.has(selection.provider?.id ?? '')
  const available = supported && usesWorkspace(selection.mode?.id)
  return {
    supported,
    available,
    reason: available
      ? 'Cloned into the private workspace at startup.'
      : 'Choose a private workspace execution to clone a repository.',
  }
}

/** Pills only offer a choice; a control with one option is a fact, not a pill. */
export function visibleLaunchControls(controls: LaunchControl[]) {
  return controls.filter((control) => control.options.length > 1)
}

/** Inherited launch defaults must be runnable in the current environment. */
export function initialLaunchSettings(
  config: BridgeConfig,
  seed: Partial<SessionConfig>,
): Partial<SessionConfig> {
  const settings = {
    ...seed,
    permission_mode: config.defaults.permission_mode ?? 'auto',
  }
  settings.acp_config_options = Object.fromEntries(
    Object.entries(settings.acp_config_options ?? {}).filter(
      ([key]) => !['permission_mode', 'approval_policy'].includes(key),
    ),
  )
  if (settings.app_id === 'puck' || settings.provider === 'puck') {
    const provider = environmentProviders(config, settings.environment).find(
      (item) => item.id === 'puck',
    )
    const models = provider?.models ?? []
    const requestedProvider = String(
      settings.acp_config_options?.provider ?? '',
    )
    const matchingModels = models.filter(
      (model) =>
        !requestedProvider || model.startsWith(requestedProvider + ':'),
    )
    const choices = matchingModels.length ? matchingModels : models
    const requestedModel = String(
      settings.acp_config_options?.model ?? settings.model ?? '',
    )
    const model = choices.includes(requestedModel) ? requestedModel : choices[0]
    const requestedEffort = String(
      settings.acp_config_options?.effort ??
        (models.includes(settings.model ?? '') ? settings.effort : '') ??
        '',
    )
    return launchSettings({
      ...settings,
      provider: 'puck',
      execution_mode: 'tool-restricted',
      acp_config_options: model
        ? {
            provider: catalogProvider(model),
            model,
            effort: provider?.model_efforts?.[model]?.includes(requestedEffort)
              ? requestedEffort
              : (provider?.model_efforts?.[model]?.[1] ?? 'default'),
          }
        : {},
    })
  }
  if (settings.app_id === 'opencode' || settings.provider === 'opencode') {
    // The first offered model is what runs unless the launch names one.
    const models =
      environmentProviders(config, settings.environment).find(
        (item) => item.id === 'opencode',
      )?.models ?? []
    const requested = String(settings.acp_config_options?.model ?? '')
    const { model: _requested, ...rest } = settings.acp_config_options ?? {}
    const model = models.includes(requested) ? requested : models[0]
    settings.acp_config_options = model ? { ...rest, model } : rest
    settings.provider = 'opencode'
  }
  if (settings.fork_session || launchSelection(config, settings).available)
    return settings
  const fallback = launchSelection(config, {
    ...settings,
    execution_mode: null,
  })
  return fallback.available
    ? { ...settings, execution_mode: fallback.mode!.id }
    : settings
}

/** Fresh launches carry provider configuration but never another session identity. */
export function launchSettings(
  settings: Partial<SessionConfig>,
): Partial<SessionConfig> {
  const {
    provider_session_id: _providerSession,
    app_binding_id: _binding,
    ...fresh
  } = settings
  if (fresh.protocol === 'ui') return fresh
  const neutral = {
    ...fresh,
    permission_mode: fresh.permission_mode ?? 'auto',
    ...(fresh.execution_mode === 'claude-managed' ? { project: '' } : {}),
    protocol: 'acp' as const,
    acp_version: 2 as const,
  }
  if (
    usesWorkspace(fresh.execution_mode) ||
    !fresh.execution_mode ||
    fresh.provider !== 'puck'
  )
    return neutral
  return {
    ...neutral,
    project: '',
    model: null,
    effort: null,
    thinking: null,
    thinking_budget_tokens: null,
    context_1m: false,
    fast_mode: false,
    permission_mode: fresh.permission_mode ?? 'auto',
    allowed_tools: [],
    disallowed_tools: [],
    system_prompt: null,
    system_prompt_append: null,
    max_turns: null,
    max_budget_usd: null,
    setting_sources: ['user', 'project', 'local'],
    add_dirs: [],
    env: {},
    plugins: [],
    sandbox_enabled: false,
    sandbox_allowed_domains: [],
    file_checkpointing: false,
    fork_session: false,
    source_session_id: null,
  }
}

function modelValue(
  model: BridgeConfig['models'][number],
  provider?: AgentProviderId,
) {
  return provider === 'claude' ? (model.aliases[0] ?? model.id) : model.id
}

/** `openai:gpt-5.2` (Puck) or `openai/gpt-5.2` (OpenCode) → `openai`. */
function catalogProvider(model: string) {
  const at = model.search(/[:/]/)
  return at < 0 ? '' : model.slice(0, at)
}

const PROVIDER_TITLES: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  'google-gla': 'Google',
}

/**
 * The models a Puck or OpenCode launch may name, as the runtime describes
 * them. A runtime that publishes only ids gets labels derived from them.
 */
export function launchModelCatalog(
  provider?: ProviderDefinition,
): ModelCatalogEntry[] {
  if (!provider) return []
  if (provider.model_catalog?.length) return provider.model_catalog
  return (provider.models ?? []).map((value) => {
    const prefix = catalogProvider(value)
    return {
      value,
      provider: prefix,
      provider_title:
        PROVIDER_TITLES[prefix] ??
        prefix.charAt(0).toUpperCase() + prefix.slice(1),
      label: modelLabel(value.slice(prefix.length + 1)),
      description: '',
      efforts: provider.model_efforts?.[value] ?? [],
    }
  })
}

export type LaunchControl = {
  id: 'execution' | 'model' | 'effort' | 'approval'
  label: string
  value: string
  options: PillOption[]
  /** Present when the model choice has a provider layer above its models. */
  groups?: PillGroup[]
  disabled: boolean
  onChange: (value: string) => void
}

/** The launch choices behind a new session as data, so a pill row and a sheet render the same controls. */
export function launchControls({
  config,
  settings,
  onChange,
  disabled = false,
  providerLocked = false,
}: {
  config: BridgeConfig
  settings: Partial<SessionConfig>
  onChange: (patch: Partial<SessionConfig>) => void
  disabled?: boolean
  providerLocked?: boolean
}): LaunchControl[] {
  const selection = launchSelection(config, settings)
  const provider = selection.provider?.id
  const choose = (patch: Partial<SessionConfig>) => {
    const next = { ...settings, ...patch }
    const resolved = launchSelection(config, next)
    onChange({
      ...patch,
      provider: resolved.provider?.id,
      execution_mode: resolved.mode?.id ?? null,
      environment: resolved.environment,
      acp_auth_method: null,
    })
  }
  const catalog = config.models.filter((model) =>
    provider === 'codex'
      ? model.provider === 'openai'
      : provider === 'claude'
        ? model.provider !== 'openai'
        : false,
  )
  const selectedModel =
    catalog.find(
      (model) =>
        model.id === settings.model ||
        model.aliases.includes(settings.model ?? ''),
    ) ?? catalog[0]
  const modelSupportsEffort = selectedModel?.supports_effort !== false
  const execution: LaunchControl = {
    id: 'execution',
    label: 'Execution',
    disabled: disabled || providerLocked || provider === 'puck',
    value: selection.mode?.id ?? '',
    onChange: (value) => choose({ execution_mode: value as ExecutionMode }),
    // Modes this environment has not enabled are hidden, not shown disabled.
    options: (selection.provider?.execution_modes ?? [])
      .filter((mode) => mode.available)
      .map((mode) => ({
        value: mode.id,
        label: mode.title,
        ...EXECUTIONS[mode.id],
      })),
  }
  const approval: LaunchControl = {
    id: 'approval',
    label: 'Approval Mode',
    disabled,
    value:
      settings.permission_mode ?? config.defaults.permission_mode ?? 'auto',
    options: Object.entries(PERMISSIONS).map(([value, option]) => ({
      ...option,
      value,
    })),
    onChange: (value) =>
      choose({ permission_mode: value as SessionConfig['permission_mode'] }),
  }
  if (provider === 'puck' || provider === 'opencode') {
    // Puck and OpenCode run models from several providers, so the model
    // choice has two layers: the provider, then its models. The provider
    // cannot change once the session exists.
    const entries = launchModelCatalog(selection.provider)
    const chosen = String(settings.acp_config_options?.model ?? '')
    const selected =
      entries.find((entry) => entry.value === chosen) ?? entries[0]
    const groups = [...new Set(entries.map((entry) => entry.provider))].map(
      (id) => ({
        value: id,
        label:
          entries.find((entry) => entry.provider === id)?.provider_title || id,
        icon: 'web' as const,
      }),
    )
    const model: LaunchControl = {
      id: 'model',
      label: 'Model',
      disabled: disabled || !entries.length,
      value: selected?.value ?? '',
      options: entries.map((entry) => ({
        value: entry.value,
        label: entry.label,
        description: entry.description || undefined,
        icon: modelIcon(entry.value),
        group: entry.provider,
      })),
      groups: groups.length > 1 ? groups : undefined,
      onChange: (value) =>
        choose({
          acp_config_options:
            provider === 'puck'
              ? {
                  ...settings.acp_config_options,
                  provider: catalogProvider(value),
                  model: value,
                  effort:
                    selection.provider?.model_efforts?.[value]?.[1] ??
                    'default',
                }
              : { ...settings.acp_config_options, model: value },
        }),
    }
    if (provider === 'opencode')
      return entries.length
        ? [execution, approval, model]
        : [execution, approval]
    const puckEfforts =
      selection.provider?.model_efforts?.[selected?.value ?? ''] ?? []
    return [
      execution,
      model,
      {
        id: 'effort',
        label: 'Effort Level',
        disabled: disabled || !puckEfforts.length,
        value: String(settings.acp_config_options?.effort ?? 'default'),
        options: (puckEfforts.length ? puckEfforts : ['default']).map(
          (effort) => ({
            value: effort,
            label: effort.charAt(0).toUpperCase() + effort.slice(1),
            icon: 'agent',
          }),
        ),
        onChange: (effort) =>
          choose({
            acp_config_options: { ...settings.acp_config_options, effort },
          }),
      },
    ]
  }
  return [
    execution,
    approval,
    {
      id: 'model',
      label: 'Model',
      disabled: disabled || !catalog.length,
      value: selectedModel ? modelValue(selectedModel, provider) : '',
      options: catalog.map((model) => ({
        value: modelValue(model, provider),
        label: model.label,
        description: model.description,
        icon: modelIcon(model.id),
      })),
      onChange: (value) => choose({ model: value }),
    },
    {
      id: 'effort',
      label: 'Effort Level',
      disabled: disabled || !modelSupportsEffort,
      value: settings.effort ?? config.defaults.effort ?? 'high',
      options: config.efforts.map((effort) => ({
        ...EFFORTS[effort.id],
        value: effort.id,
        label: effort.label,
        short: effort.short,
        description: effort.description,
      })),
      onChange: (value) => choose({ effort: value as SessionConfig['effort'] }),
    },
  ]
}

/** The values behind the "+" menu, joined for the phone launcher's summary line. */
export function launchSummary(controls: LaunchControl[]): string {
  return controls
    .filter((control) => control.id !== 'model')
    .map((control) => {
      const chosen = control.options.find(
        (option) => option.value === control.value,
      )
      return chosen?.short ?? chosen?.label
    })
    .filter(Boolean)
    .join(' · ')
}

/**
 * Compact launch controls shown inside the first-message composer. `inline`
 * lays every choice out as a pill; `compact` shows only the model, the way
 * the phone launcher keeps the rest behind its "+" menu; `rows` lists them
 * with labels for that menu.
 */
export function LaunchPills({
  config,
  settings,
  onChange,
  disabled = false,
  providerLocked = false,
  variant = 'inline',
}: {
  config: BridgeConfig
  settings: Partial<SessionConfig>
  onChange: (patch: Partial<SessionConfig>) => void
  disabled?: boolean
  providerLocked?: boolean
  variant?: 'inline' | 'compact' | 'rows'
}) {
  const controls = visibleLaunchControls(
    launchControls({
      config,
      settings,
      onChange,
      disabled,
      providerLocked,
    }),
  )
  const shown =
    variant === 'compact'
      ? controls.filter((control) => control.id === 'model')
      : variant === 'rows'
        ? controls.filter((control) => control.id !== 'model')
        : controls
  if (variant === 'rows')
    return (
      <div className="m-option-list" aria-label="Session options">
        {shown.map((control) => (
          <div key={control.id} className="m-option-row">
            <span className="m-option-label">{control.label}</span>
            <PillMenu
              label={control.label}
              showLabel={false}
              tone="field"
              align="right"
              valueLabel="full"
              disabled={control.disabled}
              value={control.value}
              options={control.options}
              groups={control.groups}
              onChange={control.onChange}
            />
          </div>
        ))}
      </div>
    )
  return (
    <div className="new-session-pills" aria-label="Session options">
      {shown.map((control) => (
        <PillMenu
          key={control.id}
          label={control.label}
          showLabel={false}
          tone="field"
          align="left"
          disabled={control.disabled}
          value={control.value}
          options={control.options}
          groups={control.groups}
          onChange={control.onChange}
        />
      ))}
    </div>
  )
}

/** Provider and execution choices come from the selected environment's registry. */
export function ProviderSettings({
  config,
  settings,
  onChange,
  disabled = false,
  providerLocked = false,
}: {
  config: BridgeConfig
  settings: Partial<SessionConfig>
  onChange: (patch: Partial<SessionConfig>) => void
  disabled?: boolean
  providerLocked?: boolean
}) {
  const selection = launchSelection(config, settings)
  const choose = (patch: Partial<SessionConfig>) => {
    const next = { ...settings, ...patch }
    const resolved = launchSelection(config, next)
    onChange(
      launchSettings({
        ...next,
        ...(resolved.provider?.id !== selection.provider?.id
          ? {
              model: null,
              effort: null,
              thinking: null,
              thinking_budget_tokens: null,
              fast_mode: false,
              context_1m: false,
            }
          : {}),
        provider: resolved.provider?.id,
        execution_mode: resolved.mode?.id ?? null,
        environment: resolved.environment,
        acp_config_options: {},
        acp_auth_method: null,
      }),
    )
  }
  const mode = selection.mode?.id
  return (
    <div className="provider-settings space-y-3">
      <label className="block text-xs">
        Environment
        <select
          className="control mt-1 w-full"
          aria-label="Environment"
          disabled={disabled || providerLocked}
          value={selection.environment}
          onChange={(event) =>
            choose({ environment: event.target.value, execution_mode: null })
          }
        >
          {config.environments.map((item) => (
            <option key={item.name} value={item.name}>
              {item.label}
              {item.health.reachable ? '' : ' (unreachable)'}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs">
        Provider
        <select
          className="control mt-1 w-full"
          aria-label="Provider"
          disabled={disabled || providerLocked}
          value={selection.provider?.id ?? ''}
          onChange={(event) =>
            choose({
              provider: event.target.value as SessionConfig['provider'],
              execution_mode: null,
            })
          }
        >
          {!selection.providers.length && (
            <option value="">No providers available</option>
          )}
          {selection.providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.title}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs">
        Session execution
        <select
          className="control mt-1 w-full"
          aria-label="Session execution"
          disabled={disabled || providerLocked}
          value={mode ?? ''}
          onChange={(event) =>
            choose({ execution_mode: event.target.value as ExecutionMode })
          }
        >
          {!selection.provider?.execution_modes.some(
            (item) => item.available,
          ) && <option value="">No execution modes available</option>}
          {selection.provider?.execution_modes
            .filter((item) => item.available)
            .map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
        </select>
      </label>
      {!!selection.provider?.auth_methods?.length && (
        <label className="block text-xs">
          Sign in with
          <select
            className="control mt-1 w-full"
            aria-label="Sign in with"
            disabled={disabled || providerLocked}
            value={settings.acp_auth_method ?? ''}
            onChange={(event) =>
              onChange({ acp_auth_method: event.target.value || null })
            }
          >
            <option value="">Deployment default</option>
            {selection.provider.auth_methods.map((method) => (
              <option key={method.id} value={method.id}>
                {method.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="text-xs text-muted">
        {mode === 'claude-managed'
          ? 'Runs on Anthropic with the agent and environment configured for this deployment.'
          : mode === 'tool-restricted'
            ? 'No local filesystem or terminal tools. Connected tools may make changes in external services.'
            : 'Runs in a private workspace in this environment.'}
      </p>
      {providerLocked && (
        <p className="text-xs text-faint">
          Provider and execution are fixed for this session.
        </p>
      )}
      {!selection.available && (
        <p role="status" className="text-xs text-warn">
          {selection.mode?.reason ??
            'This environment has no available execution mode for the selected provider.'}
        </p>
      )}
      <p className="text-xs text-faint">
        Model and other settings are available after the agent connects.
      </p>
    </div>
  )
}
